// src/components/GenerationsCalendar.jsx

import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  forwardRef,
  memo,
} from "react";
import PropTypes from "prop-types";
import { Calendar, momentLocalizer, Views } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import { withStyles, useTheme } from "@material-ui/core/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  useMediaQuery,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  IconButton,
  Collapse,
  Divider,
  CircularProgress,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Snackbar,
  Box,
  Stepper,
  Step,
  StepLabel,
  FormControlLabel,
  Radio,
  RadioGroup,
  Fab,
  Button,
  Grid,
  DialogActions, // Import for confirmation dialog
  DialogContentText, // Import for confirmation dialog
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import BusinessIcon from "@material-ui/icons/Business";
import {
  ExpandLess,
  ExpandMore,
  ArrowBack,
  ArrowForward,
  Today as TodayIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  FilterList as FilterListIcon, // Import FilterList icon
} from "@material-ui/icons";
import MuiAlert from "@material-ui/lab/Alert";
import { debounce } from "lodash"; // Import debounce
import applicationMaterials from "./applicationMaterials";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";

// Initialize the localizer using moment.js
const localizer = momentLocalizer(moment);

// Drag and Drop Calendar
const DnDCalendar = withDragAndDrop(Calendar);

const styles = (theme) => ({
  calendarContainer: {
    height: "100%", // Ensure container takes full height if parent allows
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[1],
    overflow: "hidden", // Prevent calendar overflow issues initially
    display: "flex", // Use flexbox for layout
    flexDirection: "column", // Stack children vertically
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1),
    },
    position: "relative", // For FAB positioning
  },
  calendarWrapper: {
    flexGrow: 1, // Allow calendar to take remaining space
    minHeight: 500, // Ensure minimum height for calendar visibility
    height: "auto", // Let it grow naturally
  },
  eventStyled: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(0.5, 1),
    fontSize: "0.875rem",
    fontWeight: 500,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  dialogContentStyled: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
  },
  companyLogo: {
    marginRight: theme.spacing(2),
    width: theme.spacing(5),
    height: theme.spacing(5),
    objectFit: "contain",
  },
  listItemStyled: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  toolbarContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    "& .rbc-toolbar-label": {
      color: theme.palette.text.primary,
      fontWeight: 600,
      fontSize: "1rem",
    },
    "& button": {
      color: theme.palette.text.primary,
      backgroundColor: "transparent",
      border: "none",
      fontSize: "0.875rem",
      margin: theme.spacing(0, 0.5),
      cursor: "pointer",
      padding: theme.spacing(0.5, 1),
      borderRadius: "4px",
      transition: "background-color 0.3s",
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
      },
    },
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    minHeight: 300, // Ensure loading spinner is visible
  },
  formControlStyled: {
    minWidth: 120,
    width: "100%", // Ensure full width within grid item
  },
  alertSnackbar: {
    width: "100%",
  },
  stepper: {
    padding: theme.spacing(2, 0), // Reduced padding
    width: "100%", // Ensure stepper takes full width
  },
  radioGroupCentered: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(1), // Reduced margin
  },
  dividerStyled: {
    margin: theme.spacing(2, 0),
  },
  fab: {
    position: "absolute",
    bottom: theme.spacing(2),
    right: theme.spacing(2),
    zIndex: 1050, // Ensure FAB is above calendar elements
  },
  statsContainer: {
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    boxShadow: theme.shadows[1],
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1),
    },
  },
  filterContainer: {
    marginBottom: theme.spacing(2), // Keep margin below filters
    padding: theme.spacing(1), // Add some padding around filters
    backgroundColor: theme.palette.background.paper, // Optional: background for visual separation
    borderRadius: theme.shape.borderRadius, // Optional: rounded corners
    boxShadow: theme.shadows[1], // Optional: shadow
  },
  noResultsText: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  filterToggleButton: {
    marginBottom: theme.spacing(1), // Add space below the button
    display: "none", // Hide by default, show only on mobile
    [theme.breakpoints.down("sm")]: {
      display: "inline-flex", // Show on small screens
    },
  },
  filterContent: {
    // Styles for the collapsible filter content
    overflow: "hidden",
    transition: theme.transitions.create("max-height", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    width: "100%", // Ensure it takes full width
  },
  filterContentCollapsed: {
    maxHeight: 0,
    paddingTop: 0, // Remove padding when collapsed
    paddingBottom: 0, // Remove padding when collapsed
    marginTop: 0, // Remove margin when collapsed
    marginBottom: 0, // Remove margin when collapsed
    // Ensure visibility is handled correctly for transition
    visibility: "hidden",
  },
  filterContentExpanded: {
    maxHeight: "500px", // Adjust as needed, large enough for content
    visibility: "visible",
  },
});

// Helper function to construct domain from company name
const constructDomain = (companyName) => {
  if (!companyName) return "example.com";
  const sanitized = companyName.toLowerCase().replace(/[^a-z0-9]/g, "");
  return `${sanitized}.com`;
};

// Function to get logo URL using Clearbit Logo API with fallback
const getLogoUrl = (companyName) => {
  const domain = constructDomain(companyName);
  return `https://logo.clearbit.com/${domain}`;
};

// Helper function to check if a given generation has a certain material key
function hasMaterial(generation, key) {
  // Check top-level field first
  if (generation[key + "Json"]) return true;
  // Check content field
  if (generation.content && generation.content[key + "Json"]) return true;
  // Check if content is stored in content.items[0]
  if (
    generation.content &&
    Array.isArray(generation.content.items) &&
    generation.content.items.length > 0 &&
    generation.content.items[0][key + "Json"]
  ) {
    return true;
  }
  return false;
}

// Alert Component for Snackbar
const AlertComponent = forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// Custom Toolbar Component
const CustomToolbar = ({ onNavigate, label }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs")); // Use xs for very small screens

  const goToBack = useCallback(() => {
    onNavigate("PREV");
  }, [onNavigate]);

  const goToNext = useCallback(() => {
    onNavigate("NEXT");
  }, [onNavigate]);

  const goToToday = useCallback(() => {
    onNavigate("TODAY");
  }, [onNavigate]);

  return (
    <Box className="toolbarContainer">
      <Box display="flex" alignItems="center">
        <IconButton
          onClick={goToBack}
          aria-label="Previous Month"
          size={isMobile ? "small" : "medium"}
        >
          <ArrowBack />
        </IconButton>
        <IconButton
          onClick={goToToday}
          aria-label="Go to Today"
          size={isMobile ? "small" : "medium"}
        >
          <TodayIcon />
        </IconButton>
        <IconButton
          onClick={goToNext}
          aria-label="Next Month"
          size={isMobile ? "small" : "medium"}
        >
          <ArrowForward />
        </IconButton>
      </Box>
      <Typography variant="h6" aria-live="polite" aria-atomic="true">
        {label}
      </Typography>
      {/* View switcher could be added here if needed */}
    </Box>
  );
};

CustomToolbar.propTypes = {
  onNavigate: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
};

// Memoized ListItem component for better performance
const GenerationListItem = memo(function GenerationListItem({
  event,
  isExpanded,
  onClick,
  onDeleteRequest, // Changed prop name
  onEdit,
  onProgressChange,
  applicationMaterials,
  onMaterialSelect,
  classes,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const steps = useMemo(
    () => ["Applied", "Interview", "Offer", "Accepted"],
    []
  );

  const [activeStep, setActiveStep] = useState(event.generation.progress || 0);
  const [status, setStatus] = useState(event.generation.status || "active");

  useEffect(() => {
    setActiveStep(event.generation.progress || 0);
    setStatus(event.generation.status || "active");
  }, [event.generation.progress, event.generation.status]);

  const handleStepChange = useCallback(
    (step) => {
      setActiveStep(step);
      onProgressChange(event.generation.id, step, status);
    },
    [onProgressChange, event.generation.id, status]
  );

  const handleStatusChange = useCallback(
    (e) => {
      const newStatus = e.target.value;
      setStatus(newStatus);
      onProgressChange(event.generation.id, activeStep, newStatus);
    },
    [onProgressChange, event.generation.id, activeStep]
  );

  const materialsAvailable = useMemo(
    () =>
      Object.keys(applicationMaterials).filter((key) =>
        hasMaterial(event.generation, key)
      ),
    [event.generation, applicationMaterials]
  );

  // Added for better a11y on stepper
  const handleStepClick = useCallback(
    (index) => {
      handleStepChange(index);
    },
    [handleStepChange]
  );

  return (
    <React.Fragment key={event.id}>
      <ListItem
        button
        onClick={() => onClick(event.id)}
        aria-expanded={isExpanded}
        aria-controls={`generation-details-${event.id}`}
      >
        <ListItemIcon>
          <Avatar
            src={getLogoUrl(event.generation.companyName)}
            alt={`${event.generation.companyName} Logo`}
            style={{ marginRight: 16, width: 40, height: 40 }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = ""; // Clear broken image
            }}
          >
            <BusinessIcon /> {/* Fallback Icon */}
          </Avatar>
        </ListItemIcon>
        <ListItemText
          primary={event.generation.companyName}
          secondary={event.generation.jobTitle}
        />
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onEdit(event.generation.id);
          }} // Prevent ListItem click
          aria-label={`Edit ${event.generation.companyName} - ${event.generation.jobTitle}`}
        >
          <EditIcon />
        </IconButton>
        <IconButton
          aria-label={`Delete ${event.generation.companyName} - ${event.generation.jobTitle}`}
          onClick={(e) => {
            e.stopPropagation();
            onDeleteRequest(event.generation.id);
          }} // Use onDeleteRequest, prevent ListItem click
          color="secondary"
        >
          <DeleteIcon />
        </IconButton>
        {isExpanded ? (
          <ExpandLess aria-label="Collapse details" />
        ) : (
          <ExpandMore aria-label="Expand details" />
        )}
      </ListItem>
      <Collapse
        in={isExpanded}
        timeout="auto"
        unmountOnExit
        id={`generation-details-${event.id}`}
      >
        <List
          component="div"
          disablePadding
          style={{ paddingLeft: theme.spacing(isMobile ? 2 : 4) }}
        >
          {" "}
          {/* Indent content */}
          {status === "active" && (
            <>
              <ListItem>
                <Typography variant="subtitle2">Progress:</Typography>
              </ListItem>
              <ListItem>
                <Box display="flex" justifyContent="center" width="100%">
                  <Stepper
                    activeStep={activeStep}
                    alternativeLabel={!isMobile} // Use alternative label on larger screens
                    orientation={isMobile ? "vertical" : "horizontal"} // Vertical on mobile
                    className={classes.stepper}
                  >
                    {steps.map((label, index) => (
                      <Step
                        key={label}
                        // Make step clickable via button for better a11y
                        component="button"
                        onClick={() => handleStepClick(index)}
                        style={{
                          cursor: "pointer",
                          background: "none",
                          border: "none",
                          padding: 0,
                          textAlign: "inherit",
                          font: "inherit",
                          color: "inherit",
                        }}
                        aria-label={`Set progress to ${label}`}
                      >
                        <StepLabel>{label}</StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </Box>
              </ListItem>
            </>
          )}
          <Box className={classes.radioGroupCentered}>
            <FormControl component="fieldset">
              <RadioGroup
                row={!isMobile} // Stack vertically on mobile
                aria-label="Application Status"
                name="status"
                value={status}
                onChange={handleStatusChange}
              >
                <FormControlLabel
                  value="active"
                  control={<Radio color="primary" size="small" />}
                  label="Active"
                />
                <FormControlLabel
                  value="closed"
                  control={<Radio color="secondary" size="small" />}
                  label="Not Selected"
                />
              </RadioGroup>
            </FormControl>
          </Box>
          <Divider className={classes.dividerStyled} />
          <ListItem>
            <Typography variant="subtitle2">Available Materials:</Typography>
          </ListItem>
          {materialsAvailable.length > 0 ? (
            materialsAvailable.map((key) => {
              const MaterialIcon = applicationMaterials[key].icon;
              return (
                <ListItem
                  button
                  key={key}
                  onClick={() => onMaterialSelect(event, key)}
                  aria-label={`View ${applicationMaterials[key].title}`} // Clarify action
                >
                  <ListItemIcon>
                    <MaterialIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={`View ${applicationMaterials[key].title}`}
                  />{" "}
                  {/* Clarify action */}
                </ListItem>
              );
            })
          ) : (
            <ListItem>
              <ListItemText primary="No application materials available." />
            </ListItem>
          )}
        </List>
      </Collapse>
      <Divider component="li" />
    </React.Fragment>
  );
});

GenerationListItem.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.string.isRequired,
    generation: PropTypes.shape({
      id: PropTypes.string.isRequired,
      companyName: PropTypes.string.isRequired,
      jobTitle: PropTypes.string.isRequired,
      content: PropTypes.object.isRequired,
      progress: PropTypes.number,
      status: PropTypes.string,
    }).isRequired,
  }).isRequired,
  isExpanded: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  onDeleteRequest: PropTypes.func.isRequired, // Updated prop name
  onEdit: PropTypes.func.isRequired,
  onProgressChange: PropTypes.func.isRequired,
  applicationMaterials: PropTypes.object.isRequired,
  onMaterialSelect: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
};

function GenerationsCalendar({
  classes,
  generations,
  onSelectGeneration,
  onEditGeneration,
  onDeleteGeneration,
  onUpdateProgress,
  onAddGeneration,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // Use theme breakpoints

  const [events, setEvents] = useState([]);
  const [selectedGenerations, setSelectedGenerations] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [expandedGenerations, setExpandedGenerations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("companyName");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    open: false,
    generationId: null,
  });

  const [sortField, setSortField] = useState("timestamp"); // Default sort by date
  const [sortOrder, setSortOrder] = useState("desc"); // Default sort descending (newest first)
  const [startDateFilter, setStartDateFilter] = useState("");
  const [endDateFilter, setEndDateFilter] = useState("");

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState("");
  const [newJobTitle, setNewJobTitle] = useState("");
  const [newTimestamp, setNewTimestamp] = useState(
    moment().format("YYYY-MM-DD")
  ); // Default to today
  const [filtersVisible, setFiltersVisible] = useState(!isMobile); // Filters visible by default on desktop, hidden on mobile

  // Update filter visibility when mobile status changes
  useEffect(() => {
    setFiltersVisible(!isMobile);
  }, [isMobile]);

  const memoizedApplicationMaterials = useMemo(() => applicationMaterials, []);

  // Debounced search handler
  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearchTerm(value);
    }, 300), // 300ms delay
    [] // Empty dependency array ensures the function is stable
  );

  const handleSearchInputChange = useCallback(
    (e) => {
      debouncedSearch(e.target.value);
    },
    [debouncedSearch]
  );

  // Effect to map generations to calendar events
  useEffect(() => {
    if (!Array.isArray(generations)) {
      console.error("Generations prop should be an array.");
      setEvents([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true); // Set loading true when generations change
    const mappedEvents = generations
      .filter((generation) => {
        // Basic validation for essential fields
        if (!generation || !generation.id || !generation.timestamp) {
          console.warn("Skipping invalid generation object:", generation);
          return false;
        }
        const timestamp = parseTimestamp(generation.timestamp);
        if (!(timestamp instanceof Date) || isNaN(timestamp)) {
          console.warn(
            "Skipping generation with invalid timestamp:",
            generation
          );
          return false;
        }
        return true;
      })
      .map((generation) => {
        const timestamp = parseTimestamp(generation.timestamp);
        const startDate = timestamp;
        // Ensure end date is at least slightly after start for visibility
        const endDate = moment(startDate).add(1, "hour").toDate();
        const jobTitle = generation.jobTitle || "Untitled Job";
        const companyName = generation.companyName || "Unknown Company";

        return {
          id: generation.id,
          title: `${jobTitle} @ ${companyName}`,
          start: startDate,
          end: endDate,
          allDay: false, // Set to false if using specific times, true if just date matters
          generation, // Keep the original generation data
        };
      });

    setEvents(mappedEvents);
    setIsLoading(false);
  }, [generations]); // Rerun when generations prop changes

  // Robust timestamp parsing
  const parseTimestamp = (timestamp) => {
    if (!timestamp) return null; // Return null for invalid input
    if (timestamp instanceof Date && !isNaN(timestamp)) return timestamp;
    // Handle Firebase Timestamp objects
    if (typeof timestamp.toDate === "function") {
      try {
        const date = timestamp.toDate();
        if (date instanceof Date && !isNaN(date)) return date;
      } catch (e) {
        /* Ignore errors from invalid toDate calls */
      }
    }
    // Handle Firestore Timestamp-like objects (seconds/nanoseconds)
    if (typeof timestamp.seconds === "number") {
      const date = new Date(timestamp.seconds * 1000);
      if (date instanceof Date && !isNaN(date)) return date;
    }
    // Handle ISO strings or other parsable date strings
    if (typeof timestamp === "string") {
      const parsedDate = moment(timestamp); // Use moment for robust parsing
      if (parsedDate.isValid()) {
        return parsedDate.toDate();
      }
    }
    console.warn("Could not parse timestamp:", timestamp);
    return null; // Indicate failure to parse
  };

  // Handler for clicking on a calendar event or "show more" link
  const handleSelectSlotOrEvent = useCallback(
    (slotInfoOrEvent) => {
      let selectedDate;
      if (slotInfoOrEvent.start) {
        // Could be an event or a slot selection
        selectedDate = moment(slotInfoOrEvent.start);
      } else if (slotInfoOrEvent.slots && slotInfoOrEvent.slots.length > 0) {
        // Slot selection
        selectedDate = moment(slotInfoOrEvent.slots[0]);
      } else if (slotInfoOrEvent.date) {
        // Show more click
        selectedDate = moment(slotInfoOrEvent.date);
      } else {
        return; // Invalid input
      }

      const selected = events.filter((e) =>
        moment(e.start).isSame(selectedDate, "day")
      );

      if (selected.length > 0) {
        setSelectedGenerations(selected);
        setExpandedGenerations([]); // Collapse all items initially
        setDialogOpen(true);
      } else {
        // Optionally handle clicking on an empty day (e.g., open Add dialog for that day)
        // setNewTimestamp(selectedDate.format("YYYY-MM-DD"));
        // setAddDialogOpen(true);
      }
    },
    [events]
  );

  // Style getter for calendar events based on progress/status
  const eventPropGetter = useCallback(
    (event) => {
      if (!event || !event.generation) {
        return {};
      }
      let backgroundColor;
      let borderColor;
      if (event.generation.status === "closed") {
        backgroundColor = theme.palette.error.light;
        borderColor = theme.palette.error.dark;
      } else {
        switch (event.generation.progress) {
          case 0: // Applied
            backgroundColor = theme.palette.info.light;
            borderColor = theme.palette.info.dark;
            break;
          case 1: // Interview
            backgroundColor = theme.palette.warning.light;
            borderColor = theme.palette.warning.dark;
            break;
          case 2: // Offer
            backgroundColor = theme.palette.success.light;
            borderColor = theme.palette.success.dark;
            break;
          case 3: // Accepted
            backgroundColor = theme.palette.success.dark; // Darker for accepted
            borderColor = theme.palette.success.main;
            break;
          default:
            backgroundColor = theme.palette.grey[400]; // Default grey
            borderColor = theme.palette.grey[600];
            break;
        }
      }
      return {
        style: {
          backgroundColor,
          borderColor: borderColor,
          color: theme.palette.getContrastText(backgroundColor), // Ensure text contrast
          borderRadius: "4px", // Slightly less rounded
          padding: "2px 6px", // Adjust padding
          fontSize: "0.8rem", // Slightly smaller font
          fontWeight: 500,
          borderWidth: "1px",
          borderStyle: "solid",
          opacity: 0.9,
        },
      };
    },
    [theme]
  ); // Depend on theme

  // Toggle expansion state for a generation in the dialog list
  const handleGenerationClick = useCallback((generationId) => {
    setExpandedGenerations((prev) =>
      prev.includes(generationId)
        ? prev.filter((id) => id !== generationId)
        : [...prev, generationId]
    );
  }, []);

  // Handle progress update from the GenerationListItem
  const handleProgressChange = useCallback(
    async (generationId, step, status) => {
      try {
        // Assuming onUpdateProgress handles async state/errors and updates the main 'generations' prop
        await onUpdateProgress(generationId, step, status); // Await if it's async

        // Optimistically update local state for immediate feedback (optional but good UX)
        const updateEventState = (currentEvents) =>
          currentEvents.map((event) => {
            if (event.generation.id === generationId) {
              return {
                ...event,
                generation: {
                  ...event.generation,
                  progress: step,
                  status: status,
                },
              };
            }
            return event;
          });
        setEvents(updateEventState);
        setSelectedGenerations(updateEventState); // Also update the dialog state if open

        setSnackbar({
          open: true,
          message: "Job progress updated.",
          severity: "success",
        });
      } catch (error) {
        console.error("Error updating progress:", error);
        setSnackbar({
          open: true,
          message: `Failed to update progress: ${
            error.message || "Unknown error"
          }`,
          severity: "error",
        });
      }
    },
    [onUpdateProgress]
  );

  // Handle selecting a specific material (e.g., Resume) for a generation
  const handleMaterialSelect = useCallback(
    (event, key) => {
      // Assuming onSelectGeneration handles navigation and potential errors
      try {
        // Store selection details if needed elsewhere (consider context/state management instead of localStorage)
        // localStorage.setItem("selectedGenerationId", event.generation.id);
        // localStorage.setItem("selectedApplicationMaterial", key);
        onSelectGeneration(event.generation.id, key);
        setDialogOpen(false); // Close the dialog after selection
        // Snackbar confirmation might be redundant if navigation occurs
        // setSnackbar({ open: true, message: "Loading material...", severity: "info" });
      } catch (error) {
        console.error("Error selecting generation material:", error);
        setSnackbar({
          open: true,
          message: "Failed to load material.",
          severity: "error",
        });
      }
    },
    [onSelectGeneration]
  );

  const handleFilterTypeChange = useCallback((e) => {
    setFilterType(e.target.value);
  }, []);

  // Memoized calculation of filtered and sorted events
  const filteredAndSortedEvents = useMemo(() => {
    let result = [...events]; // Start with a copy

    // Apply text search filter
    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      result = result.filter((event) => {
        const valueToSearch = event.generation[filterType]
          ?.toString()
          .toLowerCase();
        return valueToSearch?.includes(lowerSearchTerm);
      });
    }

    // Apply date range filters
    if (startDateFilter) {
      const start = moment(startDateFilter, "YYYY-MM-DD").startOf("day");
      if (start.isValid()) {
        result = result.filter((event) =>
          moment(event.start).isSameOrAfter(start)
        );
      }
    }
    if (endDateFilter) {
      const end = moment(endDateFilter, "YYYY-MM-DD").endOf("day");
      if (end.isValid()) {
        result = result.filter((event) =>
          moment(event.start).isSameOrBefore(end)
        );
      }
    }

    // Apply sorting
    result.sort((a, b) => {
      let valA, valB;
      if (sortField === "timestamp") {
        // Sort by date (start time)
        valA = moment(a.start);
        valB = moment(b.start);
      } else {
        // Sort by companyName or jobTitle (case-insensitive)
        valA = a.generation[sortField]?.toString().toLowerCase() || "";
        valB = b.generation[sortField]?.toString().toLowerCase() || "";
      }

      if (valA < valB) return sortOrder === "asc" ? -1 : 1;
      if (valA > valB) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

    return result;
  }, [
    events,
    searchTerm,
    filterType,
    sortField,
    sortOrder,
    startDateFilter,
    endDateFilter,
  ]);

  // Close snackbar handler
  const handleCloseSnackbar = useCallback((event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  // --- Delete Confirmation Handlers ---
  const handleDeleteRequest = useCallback((generationId) => {
    setDeleteConfirmation({ open: true, generationId });
  }, []);

  const handleCloseDeleteConfirmation = useCallback(() => {
    setDeleteConfirmation({ open: false, generationId: null });
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (deleteConfirmation.generationId) {
      const generationIdToDelete = deleteConfirmation.generationId;
      try {
        // Assuming onDeleteGeneration handles async state/errors and updates the main 'generations' prop
        await onDeleteGeneration(generationIdToDelete); // Await if async

        setSnackbar({
          open: true,
          message: "Generation deleted.",
          severity: "info",
        });

        // Optimistically remove from local state
        setEvents((prev) =>
          prev.filter((ev) => ev.generation.id !== generationIdToDelete)
        );
        setSelectedGenerations((prev) => {
          const updatedSelected = prev.filter(
            (g) => g.generation.id !== generationIdToDelete
          );
          // Close the dialog if the deleted item was the last one shown for that day
          if (updatedSelected.length === 0 && dialogOpen) {
            setDialogOpen(false);
          }
          return updatedSelected;
        });
      } catch (error) {
        console.error("Error deleting generation:", error);
        setSnackbar({
          open: true,
          message: `Failed to delete generation: ${
            error.message || "Unknown error"
          }`,
          severity: "error",
        });
      }
    }
    handleCloseDeleteConfirmation();
  }, [
    deleteConfirmation.generationId,
    onDeleteGeneration,
    handleCloseDeleteConfirmation,
    dialogOpen,
  ]);
  // --- End Delete Confirmation Handlers ---

  // Determine calendar views based on screen size
  const calendarViews = useMemo(
    () =>
      isMobile
        ? [Views.AGENDA, Views.DAY]
        : [Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA],
    [isMobile]
  );
  const defaultView = useMemo(
    () => (isMobile ? Views.AGENDA : Views.MONTH),
    [isMobile]
  );
  // Key to force re-render on mobile/desktop switch if necessary (might not be needed)
  const calendarKey = useMemo(
    () => (isMobile ? "mobile" : "desktop"),
    [isMobile]
  );

  // Custom component overrides for react-big-calendar
  const customComponents = useMemo(
    () => ({
      toolbar: CustomToolbar,
      event: ({ event }) => {
        // Month/Week/Day view event rendering
        if (!event || !event.title) return null;
        return (
          <div
            className={classes.eventStyled}
            role="button"
            tabIndex={0}
            aria-label={`Event: ${event.title}`}
            title={event.title} // Tooltip for full title
          >
            {event.title}
          </div>
        );
      },
      agenda: {
        // Customize Agenda view rendering
        time: () => null, // Hide time column in agenda if not needed
        event: ({ event }) => {
          // How each event row looks in Agenda
          if (!event || !event.title) return null;
          // Could potentially render GenerationListItem directly here for consistency
          // For now, just show the title
          return (
            <span>
              <strong>{event.title}</strong>
              {/* Add more details if desired */}
            </span>
          );
        },
      },
      // Optionally hide headers if they become too cluttered on mobile
      // day: { header: () => null },
      // week: { header: () => null },
    }),
    [classes.eventStyled] // Recreate if styles change
  );

  // Memoized statistics calculation
  const stats = useMemo(() => {
    const source = filteredAndSortedEvents; // Use filtered events for stats
    const total = source.length;
    const active = source.filter(
      (ev) => ev.generation.status !== "closed"
    ).length;
    const closed = total - active; // Simpler calculation
    const progressCounts = source.reduce((acc, ev) => {
      const progress = ev.generation.progress ?? -1; // Handle undefined progress
      acc[progress] = (acc[progress] || 0) + 1;
      return acc;
    }, {});

    return {
      total,
      active,
      closed,
      applied: progressCounts[0] || 0,
      interviews: progressCounts[1] || 0,
      offers: progressCounts[2] || 0,
      accepted: progressCounts[3] || 0,
    };
  }, [filteredAndSortedEvents]); // Recalculate when filtered events change

  // Open the "Add Generation" dialog
  const handleAddClick = useCallback(() => {
    // Reset fields before opening
    setNewCompanyName("");
    setNewJobTitle("");
    setNewTimestamp(moment().format("YYYY-MM-DD")); // Reset to today
    setAddDialogOpen(true);
  }, []);

  // Handle submitting the "Add Generation" form
  const handleAddGenerationSubmit = useCallback(async () => {
    const trimmedCompany = newCompanyName.trim();
    const trimmedJob = newJobTitle.trim();

    if (!trimmedCompany || !trimmedJob || !newTimestamp) {
      setSnackbar({
        open: true,
        message: "Company, Job Title, and Date are required.",
        severity: "warning",
      });
      return;
    }

    const parsedDate = moment(newTimestamp, "YYYY-MM-DD");
    if (!parsedDate.isValid()) {
      setSnackbar({
        open: true,
        message: "Invalid date format. Please use YYYY-MM-DD.",
        severity: "error",
      });
      return;
    }

    try {
      // Assuming onAddGeneration handles async state/errors and updates the main 'generations' prop
      await onAddGeneration({
        companyName: trimmedCompany,
        jobTitle: trimmedJob,
        timestamp: parsedDate.toISOString(), // Send ISO string to backend/DB
        content: {}, // Default empty content
        status: "active",
        progress: 0, // Default progress
      });

      setSnackbar({
        open: true,
        message: "New generation added.",
        severity: "success",
      });
      setAddDialogOpen(false); // Close dialog on success
    } catch (error) {
      console.error("Error adding generation:", error);
      setSnackbar({
        open: true,
        message: `Failed to add generation: ${
          error.message || "Unknown error"
        }`,
        severity: "error",
      });
    }
  }, [newCompanyName, newJobTitle, newTimestamp, onAddGeneration]);

  // Show loading indicator while processing
  if (isLoading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress aria-label="Loading calendar data" />
      </div>
    );
  }

  return (
    <>
      <Box className={classes.calendarContainer}>
        {/* Stats Section */}
        <Box className={classes.statsContainer}>
          <Typography variant="h6" gutterBottom>
            Application Statistics
          </Typography>
          <Grid container spacing={isMobile ? 1 : 2}>
            {" "}
            {/* Less spacing on mobile */}
            {/* Adjusted grid sizing for better mobile layout */}
            <Grid item xs={6} sm={4} md={3} lg={2}>
              <Typography variant="body2">
                <strong>Total:</strong> {stats.total}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4} md={3} lg={2}>
              <Typography variant="body2">
                <strong>Active:</strong> {stats.active}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4} md={3} lg={2}>
              <Typography variant="body2">
                <strong>Not Selected:</strong> {stats.closed}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4} md={3} lg={2}>
              <Typography variant="body2">
                <strong>Applied:</strong> {stats.applied}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4} md={3} lg={2}>
              <Typography variant="body2">
                <strong>Interviews:</strong> {stats.interviews}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4} md={3} lg={2}>
              <Typography variant="body2">
                <strong>Offers:</strong> {stats.offers}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4} md={3} lg={2}>
              <Typography variant="body2">
                <strong>Accepted:</strong> {stats.accepted}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Filters Section */}
        <Box className={classes.filterContainer}>
          {/* Toggle Button for Mobile */}
          <Button
            className={classes.filterToggleButton}
            onClick={() => setFiltersVisible(!filtersVisible)}
            startIcon={<FilterListIcon />}
            aria-expanded={filtersVisible}
            aria-controls="filter-content-area"
          >
            {filtersVisible ? "Hide Filters" : "Show Filters"}
          </Button>

          {/* Collapsible Filter Content */}
          <Collapse
            in={filtersVisible}
            timeout="auto"
            id="filter-content-area"
            className={`${classes.filterContent} ${
              filtersVisible
                ? classes.filterContentExpanded
                : classes.filterContentCollapsed
            }`}
          >
            <Grid container spacing={isMobile ? 1 : 2} alignItems="center">
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <TextField
                  label="Search"
                  variant="outlined"
                  onChange={handleSearchInputChange} // Use debounced handler
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  placeholder="Search company or title..."
                  aria-label="Search generated applications by company or job title"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2} lg={2}>
                <FormControl
                  variant="outlined"
                  size="small"
                  fullWidth
                  className={classes.formControlStyled}
                >
                  <InputLabel id="filter-type-label">Filter By</InputLabel>
                  <Select
                    labelId="filter-type-label"
                    value={filterType}
                    onChange={handleFilterTypeChange}
                    label="Filter By"
                    aria-label="Select filter category: Company Name or Job Title"
                  >
                    <MenuItem value="companyName">Company Name</MenuItem>
                    <MenuItem value="jobTitle">Job Title</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={3} md={2} lg={2}>
                <TextField
                  label="Start Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  size="small"
                  value={startDateFilter}
                  onChange={(e) => setStartDateFilter(e.target.value)}
                  aria-label="Filter by start date"
                  fullWidth
                />
              </Grid>
              <Grid item xs={6} sm={3} md={2} lg={2}>
                <TextField
                  label="End Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  size="small"
                  value={endDateFilter}
                  onChange={(e) => setEndDateFilter(e.target.value)}
                  aria-label="Filter by end date"
                  fullWidth
                />
              </Grid>
              <Grid item xs={6} sm={4} md={2} lg={1}>
                <FormControl
                  variant="outlined"
                  size="small"
                  fullWidth
                  className={classes.formControlStyled}
                >
                  <InputLabel id="sort-field-label">Sort By</InputLabel>
                  <Select
                    labelId="sort-field-label"
                    value={sortField}
                    onChange={(e) => setSortField(e.target.value)}
                    label="Sort By"
                    aria-label="Select sort category: Date, Company Name, or Job Title"
                  >
                    <MenuItem value="timestamp">Date</MenuItem>
                    <MenuItem value="companyName">Company</MenuItem>
                    <MenuItem value="jobTitle">Job Title</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={4} md={2} lg={2}>
                <FormControl
                  variant="outlined"
                  size="small"
                  fullWidth
                  className={classes.formControlStyled}
                >
                  <InputLabel id="sort-order-label">Order</InputLabel>
                  <Select
                    labelId="sort-order-label"
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    label="Order"
                    aria-label="Select sort order: Ascending or Descending"
                  >
                    <MenuItem value="asc">Ascending</MenuItem>
                    <MenuItem value="desc">Descending</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Collapse>
        </Box>

        {/* Calendar Section */}
        <Box className={classes.calendarWrapper}>
          {filteredAndSortedEvents.length === 0 && !isLoading && (
            <Typography className={classes.noResultsText}>
              No matching applications found for the selected criteria.
            </Typography>
          )}
          {filteredAndSortedEvents.length > 0 && (
            <DnDCalendar
              key={calendarKey} // May help force re-render on view changes
              localizer={localizer}
              events={filteredAndSortedEvents} // Use filtered and sorted events
              startAccessor="start"
              endAccessor="end"
              selectable // Allow clicking on days/slots
              onSelectEvent={handleSelectSlotOrEvent} // Handle event clicks
              onSelectSlot={handleSelectSlotOrEvent} // Handle slot clicks (for empty days)
              onShowMore={handleSelectSlotOrEvent} // Handle "show more" clicks
              style={{ height: "100%" }} // Let calendar fill wrapper
              eventPropGetter={eventPropGetter}
              popup // Use popups for overlapping events
              views={calendarViews}
              defaultView={defaultView}
              toolbar // Use default toolbar logic with custom component
              // Drag and Drop Handlers (assuming backend update)
              onEventDrop={({ event, start, end }) => {
                // Assuming onEditGeneration handles async update and prop refresh
                onEditGeneration(event.generation.id, {
                  ...event.generation,
                  timestamp: start.toISOString(),
                });
                setSnackbar({
                  open: true,
                  message: "Application date updated.",
                  severity: "success",
                });
              }}
              // Resizing might not make sense for 'allDay' style events, disable if needed
              // onEventResize={({ event, start, end }) => {
              //   onEditGeneration(event.generation.id, { ...event.generation, start, end });
              // }}
              resizable={false} // Disable resizing if not applicable
              components={customComponents}
              messages={{
                // Customize calendar text if needed
                today: "Today",
                previous: "Back",
                next: "Next",
                month: "Month",
                week: "Week",
                day: "Day",
                agenda: "Agenda",
                date: "Date",
                time: "Time",
                event: "Event",
                showMore: (total) => `+${total} more`,
              }}
              formats={{
                // Example: Customize date formats
                agendaHeaderFormat: "ddd MMM DD",
                dayHeaderFormat: "dddd, MMMM Do",
              }}
            />
          )}
        </Box>

        {/* Add Generation FAB */}
        {onAddGeneration && (
          <Fab
            color="primary"
            aria-label="Add New Application Entry"
            className={classes.fab}
            onClick={handleAddClick}
          >
            <AddIcon />
          </Fab>
        )}
      </Box>

      {/* Dialog for showing generations on a selected day */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        aria-labelledby="generations-dialog-title"
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile} // Full screen on small devices
      >
        <DialogTitle
          disableTypography
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: theme.palette.background.paper, // Use paper background
            borderBottom: `1px solid ${theme.palette.divider}`,
            padding: theme.spacing(1, 2), // Adjust padding
          }}
        >
          <Typography variant="h6" id="generations-dialog-title">
            Applications on{" "}
            {selectedGenerations[0]?.start
              ? moment(selectedGenerations[0].start).format("MMMM Do, YYYY")
              : "Selected Date"}
          </Typography>
          <IconButton
            aria-label="Close dialog"
            onClick={() => setDialogOpen(false)}
            style={{ color: theme.palette.text.secondary }} // Use secondary text color
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent className={classes.dialogContentStyled}>
          {selectedGenerations.length === 0 ? (
            <Typography>No applications found for this date.</Typography>
          ) : (
            <List disablePadding>
              {" "}
              {/* Remove default padding */}
              {selectedGenerations.map((event) => (
                <GenerationListItem
                  key={event.id}
                  event={event}
                  isExpanded={expandedGenerations.includes(event.id)}
                  onClick={handleGenerationClick}
                  onDeleteRequest={handleDeleteRequest} // Pass delete request handler
                  onEdit={onEditGeneration} // Pass edit handler
                  onProgressChange={handleProgressChange}
                  applicationMaterials={memoizedApplicationMaterials}
                  onMaterialSelect={handleMaterialSelect}
                  classes={classes}
                />
              ))}
            </List>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog for adding a new generation */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        aria-labelledby="add-generation-dialog-title"
        fullWidth
        maxWidth="xs" // Smaller dialog for adding
        fullScreen={isMobile}
      >
        <DialogTitle id="add-generation-dialog-title">
          Add New Application Entry
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Company Name"
            variant="outlined"
            fullWidth
            margin="normal"
            value={newCompanyName}
            onChange={(e) => setNewCompanyName(e.target.value)}
            required
            aria-required="true"
          />
          <TextField
            label="Job Title"
            variant="outlined"
            fullWidth
            margin="normal"
            value={newJobTitle}
            onChange={(e) => setNewJobTitle(e.target.value)}
            required
            aria-required="true"
          />
          <TextField
            label="Date Applied"
            variant="outlined"
            fullWidth
            margin="normal"
            value={newTimestamp}
            type="date" // Use native date picker
            InputLabelProps={{ shrink: true }}
            onChange={(e) => setNewTimestamp(e.target.value)}
            required
            aria-required="true"
          />
        </DialogContent>
        <DialogActions style={{ padding: theme.spacing(1, 3, 2, 3) }}>
          {" "}
          {/* Add padding */}
          <Button onClick={() => setAddDialogOpen(false)} color="default">
            Cancel
          </Button>
          <Button
            onClick={handleAddGenerationSubmit}
            color="primary"
            variant="contained"
          >
            Add Entry
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmation.open}
        onClose={handleCloseDeleteConfirmation}
        aria-labelledby="delete-confirm-title"
        aria-describedby="delete-confirm-description"
      >
        <DialogTitle id="delete-confirm-title">Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-confirm-description">
            Are you sure you want to delete this application entry? This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteConfirmation} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="secondary" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000} // Shorter duration
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }} // Center snackbar
      >
        <AlertComponent
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          className={classes.alertSnackbar}
        >
          {snackbar.message}
        </AlertComponent>
      </Snackbar>
    </>
  );
}

GenerationsCalendar.propTypes = {
  classes: PropTypes.object.isRequired,
  generations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      companyName: PropTypes.string, // Allow potentially missing names initially
      jobTitle: PropTypes.string, // Allow potentially missing titles initially
      content: PropTypes.object, // Content might not always be present
      jobDetails: PropTypes.object,
      timestamp: PropTypes.oneOfType([
        PropTypes.instanceOf(Date),
        PropTypes.shape({ toDate: PropTypes.func }), // Firebase Timestamp
        PropTypes.shape({ seconds: PropTypes.number }), // Firestore Timestamp
        PropTypes.string, // ISO String
      ]).isRequired,
      progress: PropTypes.number, // Optional progress
      status: PropTypes.string, // Optional status
    })
  ).isRequired,
  onSelectGeneration: PropTypes.func.isRequired,
  onEditGeneration: PropTypes.func.isRequired, // Used for drag/drop date change
  onDeleteGeneration: PropTypes.func.isRequired,
  onUpdateProgress: PropTypes.func.isRequired,
  onAddGeneration: PropTypes.func, // Optional add functionality
};

export default withStyles(styles)(GenerationsCalendar);
